import i18next from 'i18next';
import moment from 'moment-timezone';

import { BUTTON_TYPES } from '../../constants';
import { COLUMN_TYPES } from '@src/routes/forecast/Forecast/SalesPlanning/datagrid/constants';
import { getUniqueValuesByKey } from '@commons/utils/dataGrid';
import {
  renderCategoryCell,
  renderNameCell,
} from '@src/routes/forecast/Forecast/SalesPlanning/datagrid/customCellRenders';
import { TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

/**
 * Formats time from SLASHED_HOUR_MINUTE_SECOND to SLASHED_HOUR_MINUTE format
 * @param {string} time - Time string in SLASHED_HOUR_MINUTE_SECOND format
 * @returns {string} Formatted time string
 */
export const formatTime = (time) =>
  moment(time, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND).format(
    TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE,
  );

/**
 * Generic handler for all input blur events
 * Converts empty string to null, validates non-negative integers
 * @param {string} value - Input value
 * @param {Function} onChange - Change handler callback
 * @param {string|number} rowId - Row identifier
 * @param {string|number} timeSlotId - Time slot identifier
 */
export const handleInputBlur = (value, onChange, rowId, timeSlotId, allowDecimals = false) => {
  const newValue = value === '' ? null : allowDecimals ? parseFloat(value) : parseInt(value);

  if (newValue === null || newValue >= 0) {
    onChange(rowId, timeSlotId, newValue);
  }
};

/**
 * Handles increment/decrement button clicks
 * @param {Event} e - Mouse event
 * @param {string} type - Button type (increment/decrement)
 * @param {Function} setInputValue - State setter for input value
 */
export const handleButtonClick = (e, type, setInputValue) => {
  e.preventDefault();
  e.stopPropagation();

  const inputElement = e.target.closest('[data-input-container]')?.querySelector('input');

  if (inputElement) {
    const currentValue = parseInt(inputElement.value) || 0;

    if (type === BUTTON_TYPES.DECREMENT && currentValue - 1 < 0) {
      return;
    }

    const newValue = type === BUTTON_TYPES.INCREMENT ? currentValue + 1 : currentValue - 1;

    inputElement.value = newValue;
    setInputValue(newValue);
  }
};

/**
 * Helper function to check if a specific field has been modified
 * @param {*} currentValue - Current field value
 * @param {Object} originalData - Original data object
 * @param {string|number} timeSlotId - Time slot identifier
 * @param {string} fieldKey - Field key to check
 * @returns {boolean} True if field has been modified
 */
export const isFieldModified = (currentValue, originalData, timeSlotId, fieldKey) => {
  if (!originalData?.dataByTimeSlotIds?.[timeSlotId] && !!currentValue) {
    return true;
  }

  if (
    !originalData ||
    !originalData.dataByTimeSlotIds ||
    !originalData.dataByTimeSlotIds[timeSlotId]
  ) {
    return false;
  }

  const originalValue = originalData.dataByTimeSlotIds[timeSlotId][fieldKey];
  return currentValue !== originalValue;
};

export const getSharedColumns = (commonColumnsProps, formattedData, type) => {
  const nameColumn = {
    ...commonColumnsProps,
    field: 'name',
    headerName: i18next.t('GENERAL.NAME'),
    minWidth: 320,
    maxWidth: 320,
    width: 320,
    renderCell: ({ row }) =>
      renderNameCell(row.name, type, {
        withImg: true,
        imgSrc: row.img,
        ...(type === COLUMN_TYPES.PRODUCTS ? { sku: row.sku } : {}),
      }),
    sortable: true,
    resizable: false,
  };

  const categoryColumn = {
    ...commonColumnsProps,
    field: 'category',
    headerName: i18next.t('GENERAL.CATEGORY'),
    minWidth: 200,
    maxWidth: 200,
    width: 200,
    renderCell: ({ row }) => renderCategoryCell(row.category),
    filterable: true,
    sortable: true,
    resizable: false,
    type: 'singleSelect',
    valueOptions: () => {
      const uniqueCategories = getUniqueValuesByKey(formattedData, 'category');

      return uniqueCategories;
    },
    getApplyQuickFilterFn: (value) => {
      if (!value) {
        return () => true;
      }

      return ({ value: categoryValue }) => {
        const searchValue = value.toLowerCase();

        // Handle null/undefined values - show them when searching for "none" or the translated "none" value
        if (!categoryValue) {
          const translatedNoneValue = i18next.t('GENERAL.NONE_VALUE').toLowerCase();
          return translatedNoneValue.includes(searchValue);
        }

        // Handle regular category values
        return categoryValue.toLowerCase().includes(searchValue);
      };
    },
  };

  return { nameColumn, categoryColumn };
};
